import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useT } from "@/i18n";
import { BookOpen, Route, Globe2, Sparkles, X } from "lucide-react";

const CountUp = ({ value, delay }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 900;
    const kickoff = setTimeout(() => {
      const tick = (t) => {
        const p = Math.min(1, (t - start) / dur);
        setN(Math.round(p * value));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);
    return () => {
      clearTimeout(kickoff);
      cancelAnimationFrame(raf);
    };
  }, [value, delay]);
  return <span>{n}</span>;
};

export const WelcomeBack = ({ stats, name, onClose }) => {
  const { t } = useT();
  const items = [
    { icon: BookOpen, label: t("welcome.journals"), value: stats.journals, color: "#D4AF37" },
    { icon: Route, label: t("welcome.journeys"), value: stats.journeys, color: "#9D4CDD" },
    { icon: Globe2, label: t("welcome.shares"), value: stats.shares, color: "#2EC4B6" },
    { icon: Sparkles, label: t("welcome.creatures"), value: stats.creatures, color: "#FF7A45" },
  ];
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-[#050814]/85 p-4 backdrop-blur-md"
        data-testid="welcome-back-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 22 }}
          className="crystal relative w-full max-w-lg border border-[#D4AF37]/40 bg-gradient-to-br from-[#0A0F1D] to-[#0A0F1D]/70 p-8 md:p-10"
          style={{ boxShadow: "0 0 60px rgba(212,175,55,0.25)" }}
        >
          <button
            onClick={onClose}
            data-testid="welcome-close-button"
            className="absolute right-4 top-4 text-[#A3B1C6] transition-colors hover:text-[#D4AF37]"
          >
            <X className="h-5 w-5" />
          </button>

          <motion.div
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-[#D4AF37]/50"
            style={{ background: "rgba(212,175,55,0.12)", boxShadow: "0 0 30px rgba(212,175,55,0.5)" }}
          >
            <Sparkles className="h-8 w-8 text-[#D4AF37]" />
          </motion.div>

          <h2 className="text-center font-serif text-3xl font-semibold gold-text">{t("welcome.title")}</h2>
          <p className="mt-2 text-center text-sm text-[#A3B1C6]">
            {t("welcome.desc", { name: name ? `${name}, ` : "" })}
          </p>

          <div className="mt-6 grid grid-cols-2 gap-3">
            {items.map((it, i) => (
              <motion.div
                key={it.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 + i * 0.12 }}
                className="rounded-xl border border-white/10 bg-black/30 p-4 text-center"
              >
                <it.icon className="mx-auto mb-1 h-5 w-5" style={{ color: it.color }} />
                <p className="font-serif text-4xl font-semibold" style={{ color: it.color }}>
                  <CountUp value={it.value || 0} delay={400 + i * 120} />
                </p>
                <p className="text-xs text-[#A3B1C6]">{it.label}</p>
              </motion.div>
            ))}
          </div>

          <button
            onClick={onClose}
            data-testid="welcome-continue-button"
            className="crystal mt-6 w-full bg-gradient-to-r from-[#D4AF37] to-[#f0d787] py-3 font-medium text-[#050814] transition-opacity hover:opacity-90"
          >
            {t("welcome.continue")}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
